/**
 * Which cabinets carry cabinet trim (claude-docs/2026-10-30-venus-full-line-design.md,
 * "Where crown goes", "Where light rail goes"). One rule each, shared by the
 * run's stamp, the pieces, the panel and the estimate. Pure.
 */
const {
  isUpperCabinet,
  isOverFridgeCabinet,
} = require('../kitchen-assembly/overFridgeCap');
const { isTallBody } = require('../cabinet-runs/runDepth');
const { isWoodHood } = require('../fixture-types/hoodForm');
const { woodHoodShapeOf } = require('../cabinet-runs/woodHoods');

/**
 * Crown rides a run's upper and tall cabinets: every upper (corner, blind,
 * bridge and the over-fridge unit included), a tall body standing on the floor
 * (pantry, oven column), the fridge column's end panels, a Venus box wood
 * hood (a cabinet box over the range — not the curved one, whose top is a
 * narrow chimney), the header panel over a fridge, and a valance over the
 * sink (no object
 * of the room: `valances.valanceCarriers` stands it in, so the crown runs
 * across). Never a base.
 */
function isCrownCarrier(o) {
  if (!o) return false;
  // floating shelves are boards on the wall, no box to crown; an end shelf
  // curves round the run's end, where a straight crown cannot follow
  if (o.category?.floatingShelves || o.category?.endShelf) return false;
  // a valance bay is the valance's handle: its crown is the valance's own
  // carrier (`valances.valanceCarriers`)
  if (o.category?.valanceBay) return false;
  // in a rack's stack only the box on the top line carries crown
  // (`rackStacks.js`): not a rack under its cabinet, nor a cabinet under its
  // rack
  if (o.rackStack === 'above' || o.category?.rackCompanion === 'below')
    return false;
  if (isUpperCabinet(o)) return true;
  if (o.type === 'cabinet-valance') return true;
  // the header panel over a fridge no cabinet fits over carries the crown
  // across the column, as the cabinet there would (`fridgeHeader.js`)
  if (o.category?.fridgeHeader) return true;
  if (o.type === 'fridge-surround') return true;
  if (isWoodHood(o)) return woodHoodShapeOf(o) === 'box';
  return o.type === 'lower-cabinet' && isTallBody(o);
}

/**
 * Light rail runs under the uppers that hang over a counter: not the
 * over-fridge unit (the fridge is under it), and on stacked uppers only the
 * lower row (a stacked row stands on the uppers' line).
 */
function isLightRailCarrier(o) {
  return (
    isUpperCabinet(o) &&
    !isOverFridgeCabinet(o) &&
    !o?.category?.stack &&
    !o?.category?.floatingShelves &&
    !o?.category?.endShelf &&
    // it stands on the counter: no underside over it
    !o?.category?.wallDrawers &&
    // a valance over an open gap: no cabinet to run under
    !o?.category?.valanceBay &&
    // an angled end's underside is cut on the diagonal
    !o?.category?.angledEnd &&
    // in a rack's stack only the box on the bottom line carries it
    o?.rackStack !== 'below' &&
    o?.category?.rackCompanion !== 'above'
  );
}

module.exports = { isCrownCarrier, isLightRailCarrier };
